import { API } from '@/modules/api'
import type { TTuitionContractCartLine } from '@/types/cart'
import { defineStore } from 'pinia'
import { OrderItemType, OrderItemTypeCode } from '@/types/orderItemType'
import { BillingType } from '@/types/contract'
import type { ContractRevision, PaymentSchedule } from '@/types/contract'
import { InvoiceStatus } from '@/components/tuition/constants/InvoiceStatus'
import { useCartStore } from '@/stores/cart'
import { RevisionPaymentType } from '@/constants/RevisionPaymentType'

export const createUniqueId = (contractRevisionId: number, scheduledPaymentId: number) =>
  `${contractRevisionId}::${scheduledPaymentId}::${OrderItemTypeCode.TuitionContract}`

export const useTuitionContractStore = defineStore('tuitionContract', {
  state: () => ({
    isLoaded: {} as Record<TStudentAccountId, boolean>,
    contractRevisions: {} as Record<TStudentAccountId, ContractRevision[]>,
    unsignedContractRevisions: {} as Record<TStudentAccountId, ContractRevision[]>
  }),

  getters: {
    isLoading(state) {
      return (studentAccountId: TStudentAccountId) => !(state.isLoaded[studentAccountId] ?? false)
    },
    getContractRevisions(state) {
      return (studentAccountId: TStudentAccountId) =>
        state.contractRevisions[studentAccountId] ?? []
    },
    getActiveContractRevisions(state) {
      return (studentAccountId: TStudentAccountId) => {
        const allRevisions = state.contractRevisions[studentAccountId] ?? []
        const today = new Date()

        return allRevisions.filter(revision => {
          const contract = revision.contract

          const revisionHasBalanceDue = revision.invoiceHeaders.some(
            ih => ih.status === InvoiceStatus.BALANCE_DUE && !ih.deleted
          )
          const programYearActive = contract.programYear?.active ?? false
          const notExpired = revision.dueDate != null && new Date(revision.dueDate) >= today

          // Path A: interval billing
          // Show if it has balance due, or if PY is active and not expired
          if (contract.billingType === BillingType.Interval) {
            return revisionHasBalanceDue || (programYearActive && notExpired)
          }

          // Path B1: this contract has balance-due invoices — always show
          if (revisionHasBalanceDue) {
            return true
          }

          // Path B2: auto pay enabled + active program year + not expired
          if (contract.autoPaymentEnabled && programYearActive && notExpired) {
            return true
          }

          return false
        })
      }
    },
    getUnsignedContractRevisions(state) {
      return (studentAccountId: TStudentAccountId) =>
        state.unsignedContractRevisions[studentAccountId] ?? []
    },
    getPaymentSchedule(state) {
      return (studentAccountId: TStudentAccountId) =>
        state.contractRevisions[studentAccountId]?.flatMap(contract => contract.paymentSchedule) ??
        []
    },
    canAddPaymentToCart(state) {
      const cartStore = useCartStore()

      return (contractRevisionId: number, scheduledPaymentId: number, districtId: TDistrictId) => {
        const allPayments =
          state.contractRevisions ?? ({} as Record<TStudentAccountId, ContractRevision[]>)

        const payments = Object.values(allPayments).flatMap(contractList =>
          contractList.flatMap(contract => contract.paymentSchedule)
        )

        const payment = payments.find(
          p =>
            p.contractRevisionId === contractRevisionId &&
            p.scheduledPaymentId === scheduledPaymentId
        )
        if (!payment) return false // Not found, can’t add

        // allow license payments
        if (payment.revisionPaymentType === RevisionPaymentType.LICENSE_PAYMENT) {
          return true
        }

        // find license fee (if any) — license must be paid before scheduled payments
        const licenseFee = payments.find(
          p =>
            p.revisionPaymentType === RevisionPaymentType.LICENSE_PAYMENT &&
            p.contractRevisionId === payment.contractRevisionId
        )
        const hasUnpaidLicenseFee =
          licenseFee && Math.abs(licenseFee.amountPaid - licenseFee.amountDue) >= 0.000001

        return !hasUnpaidLicenseFee
      }
    }
  },

  actions: {
    async fetch(_districtId: TDistrictId, studentAccountId: TStudentAccountId) {
      this.isLoaded[studentAccountId] = false
      await this.fetchContractRevisions(studentAccountId)
      this.validateCart(_districtId)
      this.isLoaded[studentAccountId] = true
    },

    async fetchContractRevisions(studentAccountId: TStudentAccountId) {
      this.isLoaded[studentAccountId] = false
      const [contractRevisions, unsignedContractRevisions] = await Promise.all([
        API.Get.tuitionContract(studentAccountId),
        API.Get.unsignedContractRevisions(studentAccountId)
      ])
      this.contractRevisions[studentAccountId] = contractRevisions
      this.unsignedContractRevisions[studentAccountId] = unsignedContractRevisions
      this.isLoaded[studentAccountId] = true
    },

    // This function is used to validate the cart lines for the tuition contract store
    // It checks if the cart lines are valid compared to the data from the API, and if not, it removes them from the cart.
    // This prevents the cart from attempting to get order line estimates or checking out with invalid data.
    // TODO: this is needs to integrate scheduled payment ID according to Sam
    validateCart(districtId: TDistrictId) {
      const cartStore = useCartStore()

      for (const line of cartStore.getCartLines(districtId) as TTuitionContractCartLine[]) {
        if (line.orderItemType === OrderItemType.TuitionContract) {
          // Skip if isInvoice is false or doesn't exist
          if (!line.isInvoice) continue
          if (!line.studentAccountId) continue
          // Skip lines whose student's contract data hasn't been loaded into this store yet.
          // validateCart runs over every cart line for the district, but fetch() only loads
          // revisions for the student it was called with, and storeInitialize fires one fetch
          // per student concurrently — so a line can be reached before its student's revisions
          // land. Indexing here would throw (DTA-599); treating it as "revision not found"
          // would wrongly evict another student's valid line.
          const studentRevisions = this.contractRevisions[line.studentAccountId]
          if (!studentRevisions) continue
          const contractRevision = studentRevisions.find(contractRevision => {
            return (
              createUniqueId(contractRevision.contractRevisionId, line.scheduledPaymentId) ===
              line.uniqueId
            )
          })
          // ensure contract revision exists
          if (!contractRevision) {
            console.log('contract revision not found, removing from cart', line.contractRevisionId)
            this.removeFromCart(districtId, line.contractRevisionId, line.scheduledPaymentId)
          }
          // ensure contract revision amount is less than cart amount meaning cart amount is too high
          if (contractRevision && contractRevision.amount < line.amount) {
            console.log(
              'contract revision amount is less than cart amount, removing from cart',
              line.contractRevisionId
            )
            this.removeFromCart(districtId, line.contractRevisionId, line.scheduledPaymentId)
          }
        }
      }
    },

    addToCart(districtId: TDistrictId, contractData: Omit<TTuitionContractCartLine, 'uniqueId'>) {
      const cartStore = useCartStore()
      const uniqueId = createUniqueId(
        contractData.contractRevisionId,
        contractData.scheduledPaymentId
      )
      const existingLine = cartStore.getCartLine(districtId, uniqueId)

      if (existingLine) {
        cartStore.updateCartLine(districtId, uniqueId, contractData)
      } else {
        cartStore.addCartLine(districtId, uniqueId, { ...contractData, uniqueId })
      }
    },

    updateCartLine(
      districtId: TDistrictId,
      contractData: Partial<TTuitionContractCartLine> & {
        contractRevisionId: number
        scheduledPaymentId: number
      }
    ) {
      const cartStore = useCartStore()
      const uniqueId = createUniqueId(
        contractData.contractRevisionId,
        contractData.scheduledPaymentId
      )
      cartStore.updateCartLine(districtId, uniqueId, contractData)
    },

    removeFromCart(
      districtId: TDistrictId,
      contractRevisionId: number,
      scheduledPaymentId: number
    ) {
      const cartStore = useCartStore()
      const uniqueId = createUniqueId(contractRevisionId, scheduledPaymentId)
      cartStore.removeCartLine(districtId, uniqueId)

      // remove any that are now invalid
      const cartLines = cartStore.getCartLines(districtId)
      cartLines.forEach(line => {
        // Only validate TuitionContract items, skip other item types
        if (line.orderItemType !== OrderItemType.TuitionContract) {
          return
        }

        const disabled = !this.canAddPaymentToCart(
          (line as TTuitionContractCartLine).contractRevisionId,
          (line as TTuitionContractCartLine).scheduledPaymentId,
          districtId
        )
        // will only remove if is standard
        if (disabled && !(line as TTuitionContractCartLine).isInvoice) {
          cartStore.removeCartLine(districtId, line.uniqueId)
        }
      })
    },

    clearCartForDistrict(districtId: TDistrictId) {
      const cartStore = useCartStore()
      for (const line of cartStore.getCartLines(districtId)) {
        if (line.orderItemType === OrderItemType.TuitionContract) {
          this.removeFromCart(
            districtId,
            (line as TTuitionContractCartLine).contractRevisionId,
            (line as TTuitionContractCartLine).scheduledPaymentId
          )
        }
      }
    }
  }
})
